/**
 * Finance / caisse — the cash desk FeatureModule (00a core + 06 extensions).
 *
 * Four surfaces:
 *   /finance/caisse        — session open/close + encaissement (paiement)     [finance.caisse.read]
 *   /finance/facturation   — facture issue/read + remise application          [finance.facture.issue]
 *   /finance/coffre        — Z-freeze close + reopen, mouvement ledger,
 *                            closed-session corrections, X-report (06)         [finance.caisse.read]
 *   /finance/fidelite      — bareme editor + card accrual/reversal (06)        [finance.fidelite.read]
 *
 * Gating model. The router wraps EVERY module route in ProtectedRoute using the MODULE-level
 * requiredCapabilities (router.tsx) — it does not apply per-route caps. The screens need DIFFERENT
 * capabilities (caisse.read vs facture.issue vs fidelite.read), so a single shared module gate would
 * wrongly lock a biller out of facturation (or a cashier out of fidelite). We therefore keep the
 * module gate EMPTY (any authenticated user reaches the shell) and enforce rights two ways:
 *   1. each NAV item carries its own requiredCapabilities (so the side-nav only shows what you may
 *      open) — finance.caisse.read for the desk + coffre, finance.facture.issue for facturation,
 *      finance.fidelite.read for fidelite;
 *   2. each SCREEN renders its own ForbiddenState when its read/entry capability is missing, and
 *      every mutating action is gated by its exact server capability via useCan.
 *
 * Mutating actions, each behind its server capability (the server re-checks every request — useCan
 * only hides/disables UI; it is never the trust boundary):
 *   00a: finance.caisse.open · finance.caisse.close · finance.paiement.collect · finance.facture.issue
 *   06:  finance.caisse.close (Z-freeze) · finance.caisse.reopen · finance.mouvement.create ·
 *        finance.paiement.refund (correction) · finance.cloture.read · finance.fidelite.manage
 */
import type { FeatureModule } from "@/app";
import { CaissePage } from "./CaissePage";
import { FacturationPage } from "./FacturationPage";
import { CoffrePage } from "./CoffrePage";
import { FidelitePage } from "./FidelitePage";
import { registerFinanceMessages } from "./messages";
import { FINANCE_CAPS } from "./api";

const financeModule: FeatureModule = {
  id: "finance",
  title: "Finance / Caisse",
  // Empty module gate (see header): per-screen ForbiddenState + per-nav-item caps do the gating,
  // because the screens require different capabilities and the router applies ONE module gate.
  requiredCapabilities: [],
  routes: [
    { path: "/finance/caisse", element: <CaissePage /> },
    { path: "/finance/facturation", element: <FacturationPage /> },
    { path: "/finance/coffre", element: <CoffrePage /> },
    { path: "/finance/fidelite", element: <FidelitePage /> },
  ],
  nav: [
    {
      id: "finance-caisse",
      path: "/finance/caisse",
      label: "nav.finance.caisse",
      icon: "▦",
      order: 60,
      section: "nav.section.finance",
      requiredCapabilities: [FINANCE_CAPS.caisseRead],
    },
    {
      id: "finance-facturation",
      path: "/finance/facturation",
      label: "nav.finance.facturation",
      icon: "▤",
      order: 61,
      section: "nav.section.finance",
      requiredCapabilities: [FINANCE_CAPS.factureIssue],
    },
    {
      id: "finance-coffre",
      path: "/finance/coffre",
      label: "nav.finance.coffre",
      icon: "▣",
      order: 62,
      section: "nav.section.finance",
      requiredCapabilities: [FINANCE_CAPS.caisseRead],
    },
    {
      id: "finance-fidelite",
      path: "/finance/fidelite",
      label: "nav.finance.fidelite",
      icon: "◈",
      order: 63,
      section: "nav.section.finance",
      requiredCapabilities: [FINANCE_CAPS.fideliteRead],
    },
  ],
  onRegister() {
    registerFinanceMessages();
  },
};

export default financeModule;
